/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState, useRef, useEffect } from 'react'
import Text from '../../Text'
import Button from '../../Button'
import { TextInput } from '../../TextInput'
import ProgressHeader from '../components/ProgressHeader'
import { useCompetitionSetup } from '../context/CompetitionSetupContext'
import TrophyIcon from '../icons/TrophyIcon'
import colors from '@sportninja/common/constants/appColors'
import { trackCompetitionDivision } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CompetitionContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow-y: auto;
  max-height: 100vh;
`

const CompetitionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border-radius: 8px;
`

const CompetitionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const DivisionInputContainer = styled.div`
  width: 100%;
  background-color: ${colors.SECONDARY_300};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

/**
 * Single Division Step component - Step 2 of the competition setup wizard for single division structure
 * Allows users to name their single division
 */
const SingleDivisionStep: React.FC = () => {
  const {
    state,
    setSingleDivisionName,
    nextStep,
    previousStep,
    skipWizard,
    competitionName,
  } = useCompetitionSetup()
  
  const [divisionName, setDivisionName] = useState(state.singleDivisionName || '')

  // Ref for auto-focus
  const divisionInputRef = useRef<any>(null)

  const handleNext = () => {
    if (divisionName.trim()) {
      console.log('🔄 SingleDivisionStep: Setting division name:', divisionName.trim())
      
      // Track division setup
      trackCompetitionDivision(divisionName.trim())
      
      // Save division name to context
      setSingleDivisionName(divisionName.trim())
      nextStep()
    }
  }

  const handleBack = () => {
    console.log('🔄 SingleDivisionStep: Going back to structure step')
    previousStep()
  }

  const handleDivisionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (divisionName.trim()) {
        handleNext()
      }
    }
  }

  // Auto-focus when component mounts
  useEffect(() => {
    if (divisionInputRef.current) {
      divisionInputRef.current.focus()
    }
  }, [])

  return (
    <StepContainer>
      <ProgressHeader currentStep={2} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Name Your Division
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Give your single division a custom name
          </Text>
        </TitleSection>

        <CompetitionContainer>
          <CompetitionHeader>
            <CompetitionIcon>
              <TrophyIcon color={colors.PRIMARY} size={40} />
            </CompetitionIcon>
            <div>
              <Text
                variant='paragraphMedium'
                weight='bold'
                color={colors.NEUTRAL_0}
              >
                {competitionName}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Competition
              </Text>
            </div>
          </CompetitionHeader>

          <DivisionInputContainer>
              <Text
                variant='paragraphSmall'
                weight='semiBold'
                color={colors.NEUTRAL_0}
              >
                Division Name
              </Text>
              <TextInput
                ref={divisionInputRef}
                name='division_name'
                value={divisionName}
                setValue={setDivisionName}
                fieldErrors={{}}
                customError={null}
                label=''
                placeholder='Enter a name for your division'
                onKeyDown={handleDivisionKeyDown}
              />
          </DivisionInputContainer>
        </CompetitionContainer>

        <ActionsContainer>
          <div
            css={css`
              display: flex;
              gap: 16px;
              align-items: center;
              width: 100%;
            `}
          >
            <Button
              text='BACK'
              onClick={handleBack}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text='NEXT'
              onClick={handleNext}
              variant='primary'
              uppercase
              disabled={!divisionName.trim()}
              style={css`
                flex: 1;
              `}
            />
          </div>
          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default SingleDivisionStep